import type * as React from "react"
import { hueFor, initials } from "@/components/app/user-avatar"
import { cn } from "@/lib/utils"

/** True when the icon is a short pictograph (emoji) rather than a word. */
export function isEmojiIcon(icon: string | null | undefined): boolean {
  if (!icon) return false
  return /\p{Extended_Pictographic}/u.test(icon) && [...icon].length <= 8
}

/** Square agent mark: the agent's emoji icon or coloured initials. */
export function AgentAvatar({
  name,
  icon,
  size = "default",
  className,
  archived,
}: {
  name: string
  icon?: string | null
  size?: "sm" | "default" | "lg"
  className?: string
  archived?: boolean
}) {
  const hue = hueFor(name)
  const emoji = isEmojiIcon(icon)
  return (
    <span
      aria-hidden
      data-slot="agent-avatar"
      className={cn(
        "inline-flex shrink-0 items-center justify-center rounded-lg font-semibold ring-1 ring-foreground/5 select-none",
        size === "sm" && "size-6 text-[0.65rem]",
        size === "default" && "size-8 text-xs",
        size === "lg" && "size-11 text-sm",
        emoji && (size === "lg" ? "text-2xl" : size === "sm" ? "text-sm" : "text-base"),
        emoji
          ? "bg-muted"
          : "bg-[oklch(0.92_0.035_var(--h))] text-[oklch(0.4_0.075_var(--h))] dark:bg-[oklch(0.32_0.045_var(--h))] dark:text-[oklch(0.88_0.05_var(--h))]",
        archived && "opacity-60 grayscale",
        className,
      )}
      style={{ "--h": hue } as React.CSSProperties}
    >
      {emoji ? icon : initials(name.replace(/[^\p{L}\p{N}\s._-]/gu, "") || name)}
    </span>
  )
}
